import { CircleAlert, CircleCheck } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Icon } from './icon';
import { Spinner } from './spinner';

export type StatusTextTone = 'saving' | 'saved' | 'failed';

export interface StatusTextProps extends Omit<ComponentProps<'output'>, 'children'> {
  /** 상태가 없으면 자리만 남기고 아무것도 읽지 않는다. */
  tone?: StatusTextTone;
  /** 문구는 S·AC와 D5가 정한다(E14 §13). 예: "저장 중…" · "저장했어요" · "저장하지 못했어요" */
  children?: ReactNode;
}

const TONE_CLASS: Record<StatusTextTone, string> = {
  saving: 'text-ink-muted',
  saved: 'text-success',
  failed: 'text-danger',
};

/**
 * 그 자리에 남는 저장 상태 한 줄 — 자동 저장처럼 자주 일어나는 결과는 토스트가 아니라 이것이다(D4-20).
 * 색만으로 알리지 않고 아이콘과 글자가 함께 바뀐다. 상태가 바뀌어도 움직이지 않는다.
 */
export function StatusText({ tone, children, className, ...props }: StatusTextProps) {
  return (
    <output
      aria-live="polite"
      className={cn(
        'gap-tight inline-flex items-center text-xs',
        tone ? TONE_CLASS[tone] : null,
        className,
      )}
      data-tone={tone}
      {...props}
    >
      {tone === 'saving' ? <Spinner size="sm" decorative /> : null}
      {tone === 'saved' ? <Icon as={CircleCheck} size="sm" /> : null}
      {tone === 'failed' ? <Icon as={CircleAlert} size="sm" /> : null}
      {tone ? children : null}
    </output>
  );
}
